import { put, take, fork, takeLatest, call, select } from 'redux-saga/effects'
import _ from 'lodash'
import { Action } from 'redux'
import { handleRequest } from './requests'
import { STATE_KEYS } from '../state/requests'
import {
    CampaignTemplatesRequest,
    CampaignTemplatesResponse,
    getCampaignTemplates,
    SaveTemplateFiltersRequest,
    saveTemplateFiltersAndCreateCampaign,
    TemplateDetailRequest,
    TemplateDetail,
    getTemplateDetail,
    getTemplateData,
    TemplateDataRequest,
    TemplateData,
    switchTemplate,
    SwitchTemplateRequest,
    SwitchTemplateResponse,
    getCampaignInfo,
    CampaignInfoRequest,
    CampaignInfo,
    getCampaignSchedule,
    CampaignScheduleRequest,
    CampaignSchedule,
    CampaignTaskElementsRequest,
    CampaignTaskElements,
    getCampaignTaskElements,
    UpdateCampaignInfo,
    updateCampaignInfo,
    UpdateCampaignSchedule,
    updateCampaignSchedule,
    UpdateCampaignTaskElementsRequest,
    updateCampaignTaskElements,
    submitCampaignTemplate,
    getPaymentSummary,
    TemplateRequest,
    TemplatePaymentSummaryData,
    UpdatePaymentSummaryPayload,
    updatePaymentSummary,
    UpdateTemplateDataRequest,
    updateTemplateData,
    sendToEdit,
    requestPayment,
    sendToReview,
    publishTemplate,
    exportTemplate,
    GetPaymentSummaryPayload,
} from '../services/api/campaignTemplates'
import {
    CampaignTemplatesActions,
    saveAllTemplates,
    saveFilteredTemplates,
    TemplateQueryType,
    FilterParameters,
    CreateCampaignByTemplatePayload,
    saveTemplateDetail,
    saveTemplateData,
    clearTemplateFilter,
    SwitchTemplatePayload,
    saveCampaignInfo,
    saveCampaignSchedule,
    saveCampaignTaskElements,
    updateTemplateUuid,
    saveTemplatePaymentSummary,
    PayloadTemplateCallback,
    PayloadExportTemplate,
} from '../state/campaignTemplates'
import { PLATFORM_CONTENT_TYPE_ENUM } from '../services/api/constants'
import { getProfileDetail } from 'state/profile'
import { resetTemplateFormError, saveTemplateFormError } from 'state/error/campaignTemplates'
import { showSnackBar } from 'services/toast'
import i18n from 'config/i18n'
import { ERROR_CODES } from 'state/error'
import { selectCampaign } from 'state'
import { saveCampaign, getCampaign } from 'state/campaigns'

// todo: support paginations if design support
export function* handleGetCampaignTemplates(filter: FilterParameters, type: TemplateQueryType): any {
    const filterParameters = _.omitBy(filter, _.isNull)
    yield handleRequest<CampaignTemplatesRequest, CampaignTemplatesResponse>({
        stateKey: STATE_KEYS.GetCampaignTemplates,
        payload: filterParameters,
        ApiHandler: getCampaignTemplates,
        *onSuccess(response) {
            if (type === 'all') {
                yield put(
                    saveAllTemplates({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                    }),
                )
            } else {
                yield put(
                    saveFilteredTemplates({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                    }),
                )
            }
        },
    })
}

export function* watchCampaignTemplatesRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_TEMPLATES_REQUEST)
        const { filter, type } = action.payload
        yield fork(handleGetCampaignTemplates, filter, type)
    }
}

export function* handleCreateCampaignByTemplate(payload: CreateCampaignByTemplatePayload): any {
    const { templateFilter, onSuccessCallback, onFailCallback } = payload
    const platform_content_types = templateFilter.platform_content_types?.map(type => {
        if (type.indexOf(':') !== -1) {
            const splittedFilter = type.split(':')
            const isUGC = splittedFilter.indexOf('ugc') !== -1
            let isWithImage = null
            let isWithApproval = null

            if (splittedFilter.indexOf('image') !== -1 || splittedFilter.indexOf('non_image') !== -1) {
                isWithImage = splittedFilter.indexOf('image') !== -1
            }
            if (splittedFilter.indexOf('approval') !== -1 || splittedFilter.indexOf('non_approval') !== -1) {
                isWithApproval = splittedFilter.indexOf('approval') !== -1
            }
            return {
                platform_content_type: splittedFilter[0] as PLATFORM_CONTENT_TYPE_ENUM,
                create_content_required: isUGC,
                image_for_review_text_required: isWithImage,
                approval_for_review_text_required: isWithApproval,
            }
        } else {
            return {
                platform_content_type: type as PLATFORM_CONTENT_TYPE_ENUM,
                create_content_required: null,
                image_for_review_text_required: null,
                approval_for_review_text_required: null,
            }
        }
    })
    const requestPayload = {
        template: {
            uuid: templateFilter.templateUuid,
        },
        brand: {
            uuid: templateFilter.brandUuid,
        },
        objective: templateFilter.objective,
        get_product_required: templateFilter.get_product_required,
        platform_content_types: platform_content_types,
        // average_product_price: 0, // todo: confirm with Darwin about it. it's should be optional input
    }
    yield handleRequest<SaveTemplateFiltersRequest, any>({
        stateKey: STATE_KEYS.CreateCampaignByTemplate,
        payload: requestPayload,
        ApiHandler: saveTemplateFiltersAndCreateCampaign,
        *onSuccess(response) {
            yield put(clearTemplateFilter()) // clear templateFilter for redirect to template detail page
            yield put(saveTemplateData({ data: response.data !== undefined ? response.data.data : undefined }))
            yield onSuccessCallback(response.data?.data.uuid, response.data?.data.campaign.uuid)
            yield put(getProfileDetail())
        },
        *onError() {
            yield onFailCallback()
        },
    })
}

export function* watchCreateCampaignByTemplateRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CREATE_CAMPAIGN_BY_TEMPLATE)
        yield fork(handleCreateCampaignByTemplate, action.payload)
    }
}

export function* handleUpdateTemplateData(payload: UpdateTemplateDataRequest): any {
    const { showSuccessToast, ...rest } = payload
    yield handleRequest<UpdateTemplateDataRequest, any>({
        stateKey: STATE_KEYS.UpdateTemplateData,
        payload: rest,
        ApiHandler: updateTemplateData,
        *onSuccess(response) {
            yield put(getCampaign({ uuid: response.data?.data.campaign.uuid }))
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
    })
}

export function* watchUpdateTemplateDataRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_TEMPLATE_DATA)
        yield fork(handleUpdateTemplateData, action.payload)
    }
}

export function* handleGetTemplateDetail(payload: TemplateDetailRequest): any {
    yield handleRequest<TemplateDetailRequest, TemplateDetail>({
        stateKey: STATE_KEYS.GetTemplateDetail,
        payload: payload,
        ApiHandler: getTemplateDetail,
        *onSuccess(response) {
            yield put(
                saveTemplateDetail({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchTemplateDetailRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.TEMPLATE_DETAIL_REQUEST)
        yield fork(handleGetTemplateDetail, action.payload)
    }
}

export function* handleGetTemplateData(payload: TemplateDataRequest): any {
    yield handleRequest<TemplateDataRequest, TemplateData>({
        stateKey: STATE_KEYS.GetTemplateData,
        payload: payload,
        ApiHandler: getTemplateData,
        *onSuccess(response) {
            yield put(saveTemplateData({ data: response.data !== undefined ? response.data.data : undefined }))
        },
    })
}

export function* watchTemplateFiltersRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.TEMPLATE_DATA_REQUEST)
        yield fork(handleGetTemplateData, action.payload)
    }
}

export function* handleSwitchTemplate(payload: SwitchTemplatePayload): any {
    const { templateDataUuid, templateUuid } = payload
    yield handleRequest<SwitchTemplateRequest, SwitchTemplateResponse>({
        stateKey: STATE_KEYS.SwitchTemplate,
        payload: { templateDataUuid: templateDataUuid, templateUuid: templateUuid },
        ApiHandler: switchTemplate,
        *onSuccess(response) {
            if (response.data) yield put(updateTemplateUuid({ templateUuid: response.data.data.template.uuid }))
            yield payload.onSuccessCallback(payload.templateDataUuid, payload.campaignUuid)
        },
    })
}

export function* watchSwitchTemplateFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SWITCH_TEMPLATE)
        yield fork(handleSwitchTemplate, action.payload)
    }
}

export function* handleGetCampaignInfo(payload: CampaignInfoRequest): any {
    yield handleRequest<CampaignInfoRequest, CampaignInfo>({
        stateKey: STATE_KEYS.GetCampaignInfo,
        payload: payload,
        ApiHandler: getCampaignInfo,
        *onSuccess(response) {
            yield put(
                saveCampaignInfo({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignInfoRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_INFO_REQUEST)
        yield fork(handleGetCampaignInfo, action.payload)
    }
}

export function* handleUpdateCampaignInfo(payload: UpdateCampaignInfo): any {
    const { setNavigationDisable, showSuccessToast, ...restPayload } = payload
    yield handleRequest<UpdateCampaignInfo, CampaignInfo>({
        stateKey: STATE_KEYS.UpdateCampaignInfo,
        payload: restPayload,
        ApiHandler: updateCampaignInfo,
        *onSuccess(response) {
            yield put(
                saveCampaignInfo({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError() {
            yield setNavigationDisable?.(false)
        },
    })
}

interface UpdateCampaignInfoAction extends Action {
    type: typeof CampaignTemplatesActions.UPDATE_CAMPAIGN_INFO
    payload: UpdateCampaignInfo
}

export function* watchUpdateCampaignInfoFlow(): any {
    yield takeLatest(CampaignTemplatesActions.UPDATE_CAMPAIGN_INFO, function* (action: UpdateCampaignInfoAction) {
        yield fork(handleUpdateCampaignInfo, action.payload)
    })
}

export function* handleGetCampaignSchedule(payload: CampaignScheduleRequest): any {
    yield handleRequest<CampaignScheduleRequest, CampaignSchedule>({
        stateKey: STATE_KEYS.GetCampaignSchedule,
        payload: payload,
        ApiHandler: getCampaignSchedule,
        *onSuccess(response) {
            yield put(
                saveCampaignSchedule({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignScheduleRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_SCHEDULE_REQUEST)
        yield fork(handleGetCampaignSchedule, action.payload)
    }
}

export function* handleUpdateCampaignSchedule(payload: UpdateCampaignSchedule): any {
    const { setNavigationDisable, showSuccessToast, skipLoading, ...restPayload } = payload
    yield handleRequest<UpdateCampaignSchedule, CampaignSchedule>({
        stateKey: skipLoading ? STATE_KEYS.SkipLoading : STATE_KEYS.UpdateCampaignSchedule,
        payload: restPayload,
        ApiHandler: updateCampaignSchedule,
        *onSuccess(response) {
            yield put(
                saveCampaignSchedule({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )

            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError(response) {
            yield setNavigationDisable?.(false)
            // TODO temporary error message until we fix the backend logic
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.SCHEDULE_CHANGE_BACKWARD_NOT_ALLOWED)) {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.general.unknownError'),
                    type: 'error',
                })
            }
        },
    })
}

export function* watchUpdateCampaignScheduleFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_CAMPAIGN_SCHEDULE)
        yield fork(handleUpdateCampaignSchedule, action.payload)
    }
}

export function* handleGetCampaignTaskElements(payload: CampaignTaskElementsRequest): any {
    yield handleRequest<CampaignTaskElementsRequest, CampaignTaskElements[]>({
        stateKey: STATE_KEYS.GetCampaignTaskElements,
        payload: payload,
        ApiHandler: getCampaignTaskElements,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveCampaignTaskElements(response.data?.data))
            }
        },
    })
}

export function* watchCampaignTaskElementsRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_TASK_ELEMENTS_REQUEST)
        yield fork(handleGetCampaignTaskElements, action.payload)
    }
}

export function* handleUpdateCampaignTaskElements(payload: UpdateCampaignTaskElementsRequest): any {
    const { setNavigationDisable, showSuccessToast, ...restPayload } = payload
    yield handleRequest<UpdateCampaignTaskElementsRequest, CampaignTaskElements[]>({
        stateKey: STATE_KEYS.UpdateCampaignTaskElements,
        payload: restPayload,
        ApiHandler: updateCampaignTaskElements,
        *onSuccess() {
            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError() {
            yield setNavigationDisable?.(false)
        },
    })
}

export function* watchCampaignTaskElementsUpdateFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_CAMPAIGN_TASK_ELEMENTS)
        yield fork(handleUpdateCampaignTaskElements, action.payload)
    }
}

export function* handleSubmitCampaignTemplate(payload: PayloadTemplateCallback): any {
    const { onSuccessCallback, onErrorCallback, ...restPayload } = payload
    yield handleRequest<TemplateDetailRequest, object>({
        stateKey: STATE_KEYS.SubmitCampaignTemplate,
        payload: restPayload,
        ApiHandler: submitCampaignTemplate,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            }
        },
    })
}

export function* watchCampaignTemplateSubmitFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SUBMIT_CAMPAIGN)
        yield fork(handleSubmitCampaignTemplate, action.payload)
    }
}

export function* handleGetCampaignTemplatePaymentSummary(payload: GetPaymentSummaryPayload): any {
    yield handleRequest<GetPaymentSummaryPayload, TemplatePaymentSummaryData>({
        stateKey: STATE_KEYS.GetPaymentSummary,
        payload,
        ApiHandler: getPaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveTemplatePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchCampaignTemplatePaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.GET_PAYMENT_SUMMARY)
        yield fork(handleGetCampaignTemplatePaymentSummary, action.payload)
    }
}

export function* handleUpdateCampaignTemplatePaymentSummary(payload: UpdatePaymentSummaryPayload): any {
    yield handleRequest<UpdatePaymentSummaryPayload, TemplatePaymentSummaryData>({
        stateKey: STATE_KEYS.UpdatePaymentSummary,
        payload,
        ApiHandler: updatePaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveTemplatePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchUpdateCampaignTemplatePaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_PAYMENT_SUMMARY)
        yield fork(handleUpdateCampaignTemplatePaymentSummary, action.payload)
    }
}

export function* handleSendToEdit(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateSendToEdit,
        payload: restPayload,
        ApiHandler: sendToEdit,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                title = i18n.t('errorMessage.businessLogic.hasMoveToEditStates.subtext')
            }

            showSnackBar({
                type: 'error',
                title,
            })
        },
    })
}

export function* watchSendToEdit(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SEND_TO_EDIT)
        yield fork(handleSendToEdit, action.payload)
    }
}

export function* handleRequestPayment(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateRequestPayment,
        payload: restPayload,
        ApiHandler: requestPayment,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.hasMovedCampaignStatus', {
                    campaignStatus: i18n.t('statusLabel.waitingForPayment'),
                })
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.CAMPAIGN_NO_PAY_PERMISSION_USER)) {
                message = i18n.t('errorMessage.noPayCampaignPermissionInTeam')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchRequestPayment(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.REQUEST_PAYMENT)
        yield fork(handleRequestPayment, action.payload)
    }
}

export function* handleSendToReview(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateSendToReview,
        payload: restPayload,
        ApiHandler: sendToReview,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.hasMovedCampaignStatus', {
                    campaignStatus: i18n.t('statusLabel.forReviewAllCapital'),
                })
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchSendToReview(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SEND_TO_REVIEW)
        yield fork(handleSendToReview, action.payload)
    }
}

export function* handlePublishCampaignTemplate(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplatePublish,
        payload: restPayload,
        ApiHandler: publishTemplate,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.campaignMovedToLiveSubtext')
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }

            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchPublishCampaignTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.PUBLISH_TEMPLATE)
        yield fork(handlePublishCampaignTemplate, action.payload)
    }
}

export function* handleExportTemplate(payload: PayloadExportTemplate): any {
    const { onErrorCallback, onSuccessCallback, campaignUuid, ...restPayload } = payload
    const campaign = yield select(selectCampaign(campaignUuid))
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.ExportTemplate,
        payload: restPayload,
        ApiHandler: exportTemplate,
        *onSuccess() {
            if (campaign) {
                yield put(saveCampaign({ data: { ...campaign, in_template_flow: false } }))
            }
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                title = i18n.t('errorMessage.businessLogic.hasMoveToCampaignEditor.subtext')
            } else if (response?.general_errors?.reasons)
                showSnackBar({
                    type: 'error',
                    title,
                })
        },
    })
}

export function* watchExportTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.EXPORT_TEMPLATE)
        yield fork(handleExportTemplate, action.payload)
    }
}
